import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Music24QuotaKind } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../../../common/types';
import { useMyTeamRole } from '../../../team/useMyTeamRole';
import { useWorkspaceOwner } from '../../../team/useWorkspaceOwner';
import { TeamOwnerNote } from '../../../team/TeamOwnerNote';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import { NativeUpgradeNote } from '../NativeUpgradeNote/NativeUpgradeNote';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { planNameMessages } from '../../config/music24PlanNames';
import { SMLUpgradePromptHead } from './SMLUpgradePromptHead';
import { SMLUpgradePlanOptions } from './SMLUpgradePlanOptions';
import { SMLUpgradePromptActions } from './SMLUpgradePromptActions';
import { SMLUpgradeQuotaMeter } from './SMLUpgradeQuotaMeter';
import { StorePlanNote } from './StorePlanNote';
import { upgradeFeatureIcons } from './upgradeFeatureIcons';
import { useUpgradeCopy } from './useUpgradeCopy';
import { useUpgradePlanOptions } from './useUpgradePlanOptions';
import { UpgradeOffer } from './useUpgradeOffer';
import { featureMessages, formatQuotaResetDate, quotaKindMessages, upgradeMessages } from './upgradeMessages';
import styles from './SMLUpgradeOffer.module.scss';

interface SMLUpgradeOfferProps {
  feature: SMLUpgradeFeature;
  subject?: string;
  offer: UpgradeOffer;
  onClose: () => void;
  /** "Compare all plans": opens the plans window with its compare table (design a). */
  onCompare: () => void;
  /** Set together for an `M24_LIMIT_QUOTA` prompt — design b2 adds the meter. */
  limit?: number | null;
  used?: number | null;
  kind?: Music24QuotaKind | null;
  resetsAt?: string | null;
}

/** Design b: b1 (a locked feature) and b2 (a monthly quota reached). */
const SMLUpgradeOfferComponent: React.FC<SMLUpgradeOfferProps> = ({
  feature,
  subject,
  offer,
  onClose,
  onCompare,
  limit,
  used,
  kind,
  resetsAt,
}) => {
  const intl = useIntl();
  const planName = intl.formatMessage(planNameMessages[offer.plan.tier]);
  // Ruling 8: only the workspace owner manages billing. A member sees the same prompt, but no
  // checkout button at all — design o replaces it with `TeamOwnerNote`.
  const isMember = useMyTeamRole() === 'MEMBER';
  const owner = useWorkspaceOwner();
  // App Store 3.1.1 / Play billing: inside a native app's web view the app sells the plan, so
  // the prompt starts no Stripe checkout and points to the app's Subscription pane instead.
  const isNativeEmbed = useIsNativeEmbed();
  const options = useUpgradePlanOptions(feature, offer, { hidePrice: isNativeEmbed });

  const copy = useUpgradeCopy({
    feature,
    subject,
    planName,
    monthlyPrice: offer.monthlyPrice,
    hidePrice: isNativeEmbed,
    planCapabilities: offer.planCapabilities,
    currentCapabilities: offer.currentCapabilities,
  });

  // `used`/`limit`/`kind` travel together from `Music24LimitPayload` (only `M24_LIMIT_QUOTA` sets
  // them): either all three are here (b2), or the prompt is b1.
  const quota = useMemo(() => {
    if (kind === undefined || kind === null || typeof used !== 'number' || typeof limit !== 'number') {
      return null;
    }
    return { kind, used, limit, resetsAt: resetsAt ?? null };
  }, [kind, used, limit, resetsAt]);

  const title =
    quota === null
      ? copy.title
      : intl.formatMessage(upgradeMessages.quotaUsage, {
          used: quota.used,
          limit: quota.limit,
          kind: intl.formatMessage(quotaKindMessages[quota.kind]),
        });

  // A team plan below its cap buys one more artist on the same plan (pricing spec 2026-10-08 §7.4).
  // A native app's web view sells no web price, so it keeps the plan copy.
  const extraArtistPrice = isNativeEmbed ? null : offer.extraArtistPrice;
  const lede =
    extraArtistPrice === null
      ? copy.lede
      : intl.formatMessage(featureMessages.extraArtistLede, { price: extraArtistPrice });

  const resetDateText =
    quota !== null && quota.resetsAt !== null ? formatQuotaResetDate(intl, quota.resetsAt) : undefined;

  const ctaLabel = offer.isCheckoutPending
    ? intl.formatMessage(upgradeMessages.upgradeCtaBusy)
    : extraArtistPrice !== null
      ? intl.formatMessage(featureMessages.extraArtistCta, { price: extraArtistPrice })
      : intl.formatMessage(upgradeMessages.upgradeTo, { plan: planName });
  // Design o2: a member's quota prompt drops the plan-option list too — there is nothing to pick
  // towards, since there is no checkout underneath it.
  // The extra-artist offer keeps the current plan, so there is no plan to pick.
  const showPlanOptions = (!isMember || quota === null) && extraArtistPrice === null;
  // An app store bills the plan: the plan changes in that store, so the prompt starts no web checkout.
  const storeNote = !isMember && !isNativeEmbed ? offer.planStore : null;

  return (
    <div className={styles.prompt}>
      <SMLUpgradePromptHead
        icon={quota === null ? <FontAwesomeIcon icon={upgradeFeatureIcons[feature]} /> : intl.formatNumber(quota.limit)}
        title={title}
        body={lede}
        onClose={onClose}
      />

      {quota !== null && (
        <SMLUpgradeQuotaMeter
          used={quota.used}
          limit={quota.limit}
          kind={quota.kind}
          resetsAt={quota.resetsAt}
          currentPlanName={intl.formatMessage(planNameMessages[offer.currentCapabilities.plan])}
        />
      )}

      {showPlanOptions && <SMLUpgradePlanOptions options={options} onSelect={offer.selectPlan} />}

      {isMember && <TeamOwnerNote owner={owner} />}
      {!isMember && isNativeEmbed && <NativeUpgradeNote />}
      {storeNote !== null && <StorePlanNote store={storeNote} />}

      <SMLUpgradePromptActions
        primaryLabel={isMember || isNativeEmbed || storeNote !== null ? undefined : ctaLabel}
        onPrimary={offer.startCheckout}
        primaryDisabled={offer.isCheckoutPending}
        secondaryLabel={quota === null ? intl.formatMessage(upgradeMessages.compareAllPlans) : undefined}
        onSecondary={quota === null ? onCompare : undefined}
        laterLabel={
          resetDateText === undefined
            ? intl.formatMessage(upgradeMessages.notNow)
            : intl.formatMessage(upgradeMessages.quotaWaitFor, { date: resetDateText })
        }
        onLater={onClose}
      />
    </div>
  );
};

export const SMLUpgradeOffer = TypedMemo(SMLUpgradeOfferComponent);
